import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MODE_LABELS, TOPIC_LABELS, formatDate, type Mode, type Topic } from '@tarot/shared';
import { listMyReadings } from '../../lib/api';
import { useTrack } from '../../lib/track';
import { useMe } from '../auth/useMe';
import { TopicIcon } from '../../components/TopicIcon';
import { PrimaryLink } from '../../components/Buttons';

/** 지난 타로 목록. 회원 전용 — 비회원에게는 로그인 안내. 닉네임·탈퇴는 /profile */
export function HistoryPage() {
  useTrack('view:history');
  const { me, isLoading } = useMe();

  if (isLoading) return <p className="py-16 text-center text-moon-dim">불러오는 중…</p>;

  if (!me) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
        <h2 className="text-2xl font-bold">지난 타로</h2>
        <p className="text-moon-dim">
          로그인하면 봤던 타로를 저장하고
          <br />
          언제든 다시 볼 수 있어요
        </p>
        <PrimaryLink to="/login" className="max-w-xs">
          로그인하기
        </PrimaryLink>
      </div>
    );
  }

  return <ReadingList />;
}

function ReadingList() {
  const q = useQuery({ queryKey: ['readings'], queryFn: listMyReadings });

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-xl font-bold">지난 타로</h2>
      {q.isLoading && <p className="text-moon-dim">불러오는 중…</p>}
      {q.isError && <p className="text-red-300">불러오지 못했어요.</p>}
      {q.data && q.data.items.length === 0 && (
        <p className="rounded-2xl border border-white/10 bg-white/5 p-5 text-sm text-moon-dim">
          아직 저장된 타로가 없어요. 로그인 상태로 해석을 보면 자동으로 여기에 저장돼요.
        </p>
      )}
      <ul className="flex flex-col gap-2">
        {q.data?.items.map((r) => {
          const topic = TOPIC_LABELS[r.topic as Topic];
          const mode = MODE_LABELS[r.mode as Mode];
          return (
            <li key={r.id}>
              <Link
                to={`/r/${r.id}`}
                className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 p-3.5 transition hover:border-gold/50"
              >
                <TopicIcon topic={r.topic as Topic} className="h-8 w-8 shrink-0 text-gold" />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="font-semibold">{topic?.ko ?? r.topic}</span>
                  <span className="truncate text-xs text-moon-dim">
                    {mode?.ko ?? r.mode} · {formatDate(r.createdAt)}
                    {r.nicknames.length > 1 && <> · {r.nicknames.join('·')}</>}
                  </span>
                </span>
                <span className="shrink-0 text-moon-dim">›</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
